import { addDays, decodeDayKey, encodeDayKey } from './dayKey'

/**
 * The single source of truth for converting between instants and block slots.
 *
 * NOTHING ELSE IN THE CODEBASE MAY COMPUTE A SLOT INDEX. Every statistic the
 * app ever shows is downstream of this file, and a bug here is silent.
 *
 * The two rules that matter most:
 *
 *   1. `slotsPerDay` is COMPUTED PER DAY, never `1440 / precision`. A day is
 *      the real elapsed time between two consecutive day-start instants, which
 *      on a DST transition is 23h or 25h -- or, on Lord Howe Island, 23.5h or
 *      24.5h, because its DST shift is thirty minutes.
 *
 *   2. Slots are uniform durations measured forward from the day start. That
 *      single decision handles DST for free: on a spring-forward day the slots
 *      simply skip the missing wall-clock hour, and on a fall-back day the
 *      repeated hour gets its own extra slots.
 *
 * See 02-data-model.md section 7.
 */

export interface BlockSlot {
  dayKey: number
  slotIndex: number
}

export interface BlockGridOptions {
  precisionMinutes: number
  dayStartHour: number
  /** IANA zone. Defaults to the device's current zone. */
  timeZone?: string
}

export interface BlockGrid {
  readonly precisionMinutes: number
  readonly dayStartHour: number
  readonly timeZone: string

  /** DST-aware. 46 or 50 on a one-hour transition at 30-minute precision. */
  slotsPerDay(dayKey: number): number

  /** Instant (ms epoch) at which the given day begins. */
  startOfDay(dayKey: number): number

  /** Instant (ms epoch) at which the given slot begins. */
  startOfSlot(dayKey: number, slotIndex: number): number

  /** Duration of a slot in ms. Constant; DST is absorbed by the slot count. */
  readonly slotDurationMs: number

  dayKeyForInstant(ms: number): number
  slotForInstant(ms: number): BlockSlot

  /** Inclusive of both ends, in chronological order. May cross days. */
  slotRange(from: BlockSlot, to: BlockSlot): BlockSlot[]

  /** Moves forward or backward across day boundaries. */
  addSlots(slot: BlockSlot, delta: number): BlockSlot

  compareSlots(a: BlockSlot, b: BlockSlot): number
  isValidSlot(slot: BlockSlot): boolean
}

// -----------------------------------------------------------------------------
// Timezone primitives
// -----------------------------------------------------------------------------

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function wallFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    formatterCache.set(timeZone, f)
  }
  return f
}

interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** The local wall-clock reading in `timeZone` at the given instant. */
function wallClockAt(ms: number, timeZone: string): WallClock {
  const parts = wallFormatter(timeZone).formatToParts(new Date(ms))
  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type)
    return p ? Number(p.value) : 0
  }
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // Some ICU versions render midnight as hour 24 under hour12:false.
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  }
}

/** UTC offset in ms at the given instant. Positive east of Greenwich. */
function offsetMsAt(ms: number, timeZone: string): number {
  const w = wallClockAt(ms, timeZone)
  const asIfUTC = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second)
  return asIfUTC - ms
}

/**
 * The instant at which the clock in `timeZone` reads the given wall time.
 *
 * Two wall-clock times are pathological and both are handled explicitly:
 *
 *   AMBIGUOUS (fall back) -- the reading happens twice. We return the FIRST
 *   occurrence, which is the usual convention and, more importantly, is
 *   deterministic: both devices must agree without coordinating.
 *
 *   NONEXISTENT (spring forward) -- the reading never happens, because the
 *   clock jumps over it. We return the instant just after the gap, so a day
 *   whose configured start hour falls inside the gap still begins somewhere
 *   sensible rather than throwing.
 */
function instantForWallClock(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0)

  const offsetA = offsetMsAt(guess, timeZone)
  const candidateA = guess - offsetA
  const offsetB = offsetMsAt(candidateA, timeZone)
  const candidateB = guess - offsetB

  const matches = (ms: number): boolean => {
    const w = wallClockAt(ms, timeZone)
    return (
      w.year === year &&
      w.month === month &&
      w.day === day &&
      w.hour === hour &&
      w.minute === minute
    )
  }

  const valid: number[] = []
  if (matches(candidateA)) valid.push(candidateA)
  if (candidateB !== candidateA && matches(candidateB)) valid.push(candidateB)

  if (valid.length > 0) return Math.min(...valid)

  // Inside a spring-forward gap: no instant reads this wall time. Take the
  // later candidate, which lands just past the discontinuity.
  return Math.max(candidateA, candidateB)
}

// -----------------------------------------------------------------------------
// Factory
// -----------------------------------------------------------------------------

export function systemTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

const ALLOWED_PRECISIONS = [5, 10, 15, 30, 60] as const

export function createBlockGrid(options: BlockGridOptions): BlockGrid {
  const { precisionMinutes, dayStartHour } = options
  const timeZone = options.timeZone ?? systemTimeZone()

  if (!ALLOWED_PRECISIONS.includes(precisionMinutes as (typeof ALLOWED_PRECISIONS)[number])) {
    throw new Error(
      `Unsupported precision ${precisionMinutes}. Allowed: ${ALLOWED_PRECISIONS.join(', ')}`,
    )
  }
  if (!Number.isInteger(dayStartHour) || dayStartHour < 0 || dayStartHour > 23) {
    throw new Error(`dayStartHour must be an integer 0-23, got ${dayStartHour}`)
  }

  const slotDurationMs = precisionMinutes * 60_000

  // Day boundaries are hit constantly while scrolling, and each one costs
  // several Intl formatting calls. Cache per grid instance.
  const dayStartCache = new Map<number, number>()
  const slotCountCache = new Map<number, number>()

  function startOfDay(dayKey: number): number {
    const cached = dayStartCache.get(dayKey)
    if (cached !== undefined) return cached

    const { year, month, day } = decodeDayKey(dayKey)
    const ms = instantForWallClock(year, month, day, dayStartHour, 0, timeZone)
    dayStartCache.set(dayKey, ms)
    return ms
  }

  function slotsPerDay(dayKey: number): number {
    const cached = slotCountCache.get(dayKey)
    if (cached !== undefined) return cached

    const spanMs = startOfDay(addDays(dayKey, 1)) - startOfDay(dayKey)

    // Round rather than floor: a 30-minute DST shift at 60-minute precision
    // gives 23.5 slots, and the day must still tile completely.
    const count = Math.round(spanMs / slotDurationMs)
    slotCountCache.set(dayKey, count)
    return count
  }

  function startOfSlot(dayKey: number, slotIndex: number): number {
    return startOfDay(dayKey) + slotIndex * slotDurationMs
  }

  function dayKeyForInstant(ms: number): number {
    const w = wallClockAt(ms, timeZone)
    const calendarKey = encodeDayKey(w.year, w.month, w.day)

    // Before the day-start hour, the instant still belongs to yesterday.
    const candidate = w.hour < dayStartHour ? addDays(calendarKey, -1) : calendarKey

    // Near a transition the wall-clock shortcut can land one day out, so
    // confirm against the real boundaries and correct if needed.
    if (ms < startOfDay(candidate)) return addDays(candidate, -1)
    if (ms >= startOfDay(addDays(candidate, 1))) return addDays(candidate, 1)
    return candidate
  }

  function slotForInstant(ms: number): BlockSlot {
    const dayKey = dayKeyForInstant(ms)
    const offset = ms - startOfDay(dayKey)
    const raw = Math.floor(offset / slotDurationMs)
    const last = slotsPerDay(dayKey) - 1
    const slotIndex = raw < 0 ? 0 : raw > last ? last : raw
    return { dayKey, slotIndex }
  }

  function addSlots(slot: BlockSlot, delta: number): BlockSlot {
    let { dayKey, slotIndex } = slot
    let remaining = delta

    while (remaining !== 0) {
      const count = slotsPerDay(dayKey)
      const target = slotIndex + remaining

      if (target >= 0 && target < count) return { dayKey, slotIndex: target }

      if (target >= count) {
        remaining -= count - slotIndex
        dayKey = addDays(dayKey, 1)
        slotIndex = 0
      } else {
        remaining += slotIndex + 1
        dayKey = addDays(dayKey, -1)
        slotIndex = slotsPerDay(dayKey) - 1
      }
    }

    return { dayKey, slotIndex }
  }

  function compareSlots(a: BlockSlot, b: BlockSlot): number {
    if (a.dayKey !== b.dayKey) return a.dayKey < b.dayKey ? -1 : 1
    if (a.slotIndex !== b.slotIndex) return a.slotIndex < b.slotIndex ? -1 : 1
    return 0
  }

  function slotRange(from: BlockSlot, to: BlockSlot): BlockSlot[] {
    const [lo, hi] = compareSlots(from, to) <= 0 ? [from, to] : [to, from]

    const out: BlockSlot[] = []
    let dayKey = lo.dayKey
    let slotIndex = lo.slotIndex

    // Bounded so a malformed range can never spin forever.
    const LIMIT = 200_000

    while (out.length < LIMIT) {
      out.push({ dayKey, slotIndex })
      if (dayKey === hi.dayKey && slotIndex === hi.slotIndex) break

      slotIndex += 1
      if (slotIndex >= slotsPerDay(dayKey)) {
        dayKey = addDays(dayKey, 1)
        slotIndex = 0
      }
      // Ran past the end without matching: the target was out of bounds.
      if (dayKey > hi.dayKey) break
    }

    return out
  }

  function isValidSlot(slot: BlockSlot): boolean {
    return (
      Number.isInteger(slot.slotIndex) &&
      slot.slotIndex >= 0 &&
      slot.slotIndex < slotsPerDay(slot.dayKey)
    )
  }

  return {
    precisionMinutes,
    dayStartHour,
    timeZone,
    slotDurationMs,
    slotsPerDay,
    startOfDay,
    startOfSlot,
    dayKeyForInstant,
    slotForInstant,
    slotRange,
    addSlots,
    compareSlots,
    isValidSlot,
  }
}

// -----------------------------------------------------------------------------
// Display helpers
// -----------------------------------------------------------------------------

/** "09:30" in the grid's timezone, honouring the device's 12/24-hour setting. */
export function formatSlotTime(grid: BlockGrid, slot: BlockSlot, locale?: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: grid.timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(grid.startOfSlot(slot.dayKey, slot.slotIndex)))
}

/** Slots covering exactly one hour, for gutter labelling. */
export function slotsPerHour(grid: BlockGrid): number {
  return 60 / grid.precisionMinutes
}
